import type { IntlShape } from 'react-intl';

import { getIntegrateWorkflowCompanies } from '~/redux/api/methods/workflows/getIntegrateWorkflowCompanies';
import { getIntegrateWorkflowSuppliers } from '~/redux/api/methods/workflows/getIntegrateWorkflowSuppliers';

interface FieldValue {
  value?: {
    resource_id?: string;
    default_value?: string;
  };
}

interface Field {
  wellKnownKey?: string;
  value?: FieldValue;
}

interface Fields {
  [key: string]: Field;
}

interface ValidateSupplierAndCompanyParams {
  fields: Fields;
  workflowId: string;
  intl: IntlShape;
}

export const validateSupplierAndCompany = ({
  fields,
  workflowId,
  intl,
}: ValidateSupplierAndCompanyParams): Promise<string[]> => {
  const supplierField = Object.values(fields).find(
    (field) => field.wellKnownKey === 'supplier',
  );
  const companyField = Object.values(fields).find(
    (field) => field.wellKnownKey === 'company',
  );

  const validationPromises: Promise<string | null>[] = [];

  if (
    supplierField?.value?.value?.resource_id &&
    supplierField?.value?.value?.default_value
  ) {
    validationPromises.push(
      getIntegrateWorkflowSuppliers({
        workflowId,
        searchString: supplierField.value.value.default_value,
      })
        .then((suppliers) => {
          const supplierExists = suppliers.some(
            (s) => s.id === supplierField.value?.value?.resource_id,
          );
          if (!supplierExists) {
            return intl.formatMessage({
              id: 'TicketForm.supplierNotFoundError',
              defaultMessage:
                "Access to this supplier isn't available. You can't create an amendment. Contact the system administrator for help.",
              description: 'Error message when supplier is not found',
            });
          }
          return null;
        })
        .catch(() =>
          intl.formatMessage({
            id: 'TicketForm.supplierValidationError',
            defaultMessage:
              'There was an issue retrieving suppliers. Try again later or contact the system administrator.',
            description: 'Error message when supplier validation fails',
          }),
        ),
    );
  }

  if (
    companyField?.value?.value?.resource_id &&
    companyField?.value?.value?.default_value
  ) {
    validationPromises.push(
      getIntegrateWorkflowCompanies({
        searchString: companyField.value.value.default_value,
      })
        .then((companies) => {
          const companyExists = companies.some(
            (c) => c.id === companyField.value?.value?.resource_id,
          );
          if (!companyExists) {
            return intl.formatMessage({
              id: 'TicketForm.companyNotFoundError',
              defaultMessage:
                "Access to this company isn't available. You can't create an amendment. Contact the system administrator for help.",
              description: 'Error message when company is not found',
            });
          }
          return null;
        })
        .catch(() =>
          intl.formatMessage({
            id: 'TicketForm.companyValidationError',
            defaultMessage:
              'There was an issue retrieving companies. Try again later or contact the system administrator.',
            description: 'Error message when company validation fails',
          }),
        ),
    );
  }

  return Promise.all(validationPromises).then(
    (results) => results.filter(Boolean) as string[],
  );
};
